import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Grid, GridItem, Heading } from '@chakra-ui/react';

import { createLogLocal } from '../../../utils/log-local';
import {
  SupportedProcessors,
  SupportedPaymentMethods,
} from '../../../constants';
import { CardInputControl } from '../../ui-elements/CardInputControl';
import { InputControl } from '../../ui-elements/InputControl';
import { StripeCreditCardInputControl } from '../../ui-elements/StripeCreditCardInputControl';
import { CheckboxControl } from '../../ui-elements/CheckboxControl';
import { SelectControl } from '../../ui-elements/SelectControl';
import { countryList } from '../../../utils/countries';
import { PaymentProvider } from '../../providers/PaymentProvider';
import { useCommonOptionsStore } from '../../../store';
import { useTranslations } from '../../../translations/useTranslations';
import { queryPaymentMethodHash } from '../../../queries/methodHash';
import { useNextActionHandler } from '../../../handlers/nextActionHandler';

import type {
  NextAction,
  PaymentElementProps,
  PaymentElementResponse,
} from '../../../@types';
import type { CreditCardFormProps } from './creditCard.types';
import type { FieldErrors, SubmitHandler } from 'react-hook-form';

type FormInputsType = Record<string, any>;
type CreditCardDefaultProps = PaymentElementProps['guest'] & {
  cardholder: string;
};

const log = createLogLocal('CreditCardForm.tsx');

const layoutProps: Record<string, any> = {
  gap: 6,
  columnGap: 4,
};

/** If an error has one of these types, it is considered a validation error */
const validationTypes = [
  'validation',
  'required',
  'nativeValidation',
  'cardNumber',
  'valid',
  'validate',
];

// TODO Fix initFields type - it doesn't match CreditCardDefaultProps
// const initFields: CreditCardDefaultProps = {
const initFields: any = {
  firstName: '',
  lastName: '',
  email: '',
  cardholder: '',
  billingDetails: {
    billingAddress1: '',
    billingState: '',
    billingCity: '',
    billingZip: '',
    billingCountry: '',
    billingEmail: '',
  },
};

/**
 * Map possible returnable card brand IDs with name expected by Stripe.
 */
const cardBrandNamesMap = {
  american_express: 'amex',
  americanexpress: 'amex',
  cunion: 'unionpay',
  cunionpay: 'unionpay',
  c_union_pay: 'unionpay',
  union_pay: 'unionpay',
  dan: 'dankort',
  diners: 'dinersclub',
  dinerscard: 'dinersclub',
  diners_card: 'dinersclub',
  disc: 'discover',
  hiper: 'hipercard',
  master: 'mastercard',
};

const guestToBilling = (guestData: PaymentElementProps['guest']) => ({
  ...guestData?.billingDetails,
});

const repackFormData = (data: any) => {
  const billingDetails = Object.fromEntries(
    Object.keys(data)
      .filter(key => key.startsWith('billing'))
      .map(key => [key, data[key]]),
  );
  const { cardNumber, cardholder, expiry, cvc, firstName, lastName } = data;
  return {
    firstName,
    lastName,
    ...(typeof cardNumber === 'string' && {
      cardNumber: cardNumber.replace(/ /g, ''),
    }),
    cardholder,
    ...(typeof expiry === 'string' && {
      expMonth: +expiry?.split('/')[0]?.trim() || 0, // eslint-disable-line no-unsafe-optional-chaining
    }),
    ...(typeof expiry === 'string' && {
      expYear: 2000 + +expiry?.split('/')[1]?.trim() || 0, // eslint-disable-line no-unsafe-optional-chaining
    }),
    ...(typeof cvc === 'string' && { cvc }),
    billingDetails,
  };
};

const flattenDefaultValues = (props: CreditCardDefaultProps) => {
  const { billingDetails, ...rest } = props;
  return {
    ...rest,
    ...billingDetails,
  };
};

const sanitizeErrors = (errors: FieldErrors<FormInputsType>) =>
  Object.fromEntries(
    Object.entries(errors).map(([key, val]) => [key, val?.message]),
  );

const unpackErrorMessage = (
  errors: FieldErrors<FormInputsType>,
  name: string,
) =>
  errors[name] ? (errors[name]?.message ? errors[name]?.message : true) : false;

/**
 * Return brands list with standardized brand names matching Stripe format.
 * @param brands Raw brands list from payment method config.
 * @returns {string[]} Brands list using standardized names.
 */
const cleanBrands = (brands: string[]) =>
  brands.map(brand => {
    const lcBrand = brand.toLowerCase();
    if (cardBrandNamesMap[lcBrand]) return cardBrandNamesMap[lcBrand];
    return lcBrand;
  });

/**
 * Form used to collect credit card payment information from the user.
 *
 * Submits by deferring to paymentProviderRef.current.processPayment.
 * Validates form, and marks invalid fields.
 *
 * Data returned to parent (via useImperativeHandle) if ref given:
 * ```
 *   {
 *     validate: () => Promise<boolean>,
 *     submit: () => void,
 *   }
 * ```
 *
 * Access with `ref.current.submit()` and `ref.current.validate()`
 */
export const CreditCardForm = React.forwardRef(
  (
    {
      processor,
      onChange,
      onLoadStart,
      onLoadEnd,
      onError,
      onValidationError,
      onSubmit,
      guest,
      billingConfig,
      contactInformationConfig,
      currentErrorOnly = false,
      paymentMethodConfig,
      returnTokenOnly = false,
    }: CreditCardFormProps & { returnTokenOnly?: boolean },
    forwardRef: any,
  ) => {
    /**
     * Used to grab functions for validating and making a payment, based on the
     * payment processor being used (stripe, adyen, etc).
     *
     * Note: not related to DOM node access at all.
     *
     * TODO: do this with a hook or render prop instead.
     */
    const paymentProviderRef = useRef<any>();
    const submitPromiseResolve = useRef<any>(null);

    const [sameAsAddress, setSameAsAddress] = useState(false);

    const stripeTheme = useCommonOptionsStore(state => state.stripeTheme);
    const lang = useCommonOptionsStore(state => state.lang);
    const propertyId = useCommonOptionsStore(state => state.propertyId);
    const apiKey = useCommonOptionsStore(state => state.apiKey);
    const { apiUrl } = useCommonOptionsStore();
    const nextActionHandler = useNextActionHandler();

    const [t] = useTranslations(lang);

    // Extract guest data without billingDetails
    const guestWithoutBillingDetails = useMemo(() => {
      if (!guest) return {};
      const { billingDetails, ...restOfGuest } = guest;
      return restOfGuest;
    }, [guest]);

    /**
     * Form state and methods for submitting the credit card form.
     * Gets passed to FormProvider to manage form state, but also used
     * in various handlers in this component.
     */
    const formMethods = useForm<FormInputsType>({
      mode: 'onChange',
      defaultValues: {
        ...flattenDefaultValues({
          ...initFields,
          ...guestWithoutBillingDetails,
        }),
        cardholder: `${guest?.firstName} ${guest?.lastName}`.trim(),
      },
    });

    const {
      clearErrors,
      handleSubmit,
      formState,
      getValues,
      setValue,
      trigger,
    } = formMethods;

    const { errors: formErrors, isLoading, isSubmitting } = formState;

    const hasBillingConfig = useMemo(
      () => !!(billingConfig && Object.keys(billingConfig).length),
      [billingConfig],
    );

    const guestDataAsBilling = useMemo(() => guestToBilling(guest), [guest]);

    useEffect(
      function handleLoading() {
        if (onLoadStart && isLoading) onLoadStart();
        if (onLoadEnd && isLoading) onLoadEnd();
      },
      [isLoading, onLoadStart, onLoadEnd],
    );

    useEffect(
      function handleSubmitting() {
        if (onLoadStart && isSubmitting) onLoadStart();
        if (onLoadEnd && isSubmitting) onLoadEnd();
      },
      [isSubmitting, onLoadStart, onLoadEnd],
    );

    // Detect errors and handle them by passing to onError prop.
    useEffect(
      function handleErrors() {
        log('useEffect->handleErrors', 'formState:', formState);
        if (
          onError &&
          formState.errors &&
          Object.keys(formState.errors).length !== 0
        ) {
          log('useEffect->handleErrors', 'onError', onError);

          const errors = {};
          const validationErrors = {};

          Object.entries(formState.errors).forEach(([errName, errObj]) => {
            // Handle validation/required errors separately
            if (validationTypes.includes(errObj.type as any)) {
              validationErrors[errName] = errObj.message;
            } else {
              errors[errName] = errObj.message;
            }
          });

          log('useEffect->handleErrors', 'errors', errors);
          log('useEffect->handleErrors', 'validationErrors', validationErrors);

          // Handle regular errors.
          if (onError && Object.keys(errors).length > 0) onError(errors);

          // Handle validation errors.
          if (onValidationError && Object.keys(validationErrors).length > 0) {
            onValidationError(validationErrors);
          }
        }
      },
      [formState, onError, onValidationError],
    );

    const onFieldChange = async (fieldName: string, brand?: string) => {
      log('onFieldChange', 'fieldName:', fieldName, 'brand:', brand);
      const values = getValues(fieldName);
      log('onFieldChange', 'values:', values);
      if (onChange) onChange({ [fieldName]: values }, brand || undefined);
    };

    const onFieldBlur = async (fieldName: string) => {
      if (currentErrorOnly) clearErrors();
      await trigger([fieldName]);
    };

    const onSameAsAddressChange = (isChecked: any) => {
      const billingFields = isChecked
        ? guestDataAsBilling
        : { ...initFields.billingDetails };
      Object.entries(billingFields).forEach(([key, val]) => {
        setValue(key, val);
      });
      setSameAsAddress(isChecked);
    };

    /**
     * Submits credit card form data.
     * @param formData Form data to submit
     */
    const submitPayment: SubmitHandler<FormInputsType> = async (
      formData: any,
    ) => {
      console.info('Processing payment.');
      log('submitPayment', 'formData:', formData);

      const isValid = await trigger();
      if (!isValid) return;

      const data = repackFormData(formData);
      log('submitPayment', 'data:', data);

      /** @TODO Remove and replace with createPaymentMethod */
      const tokenResponse =
        await paymentProviderRef.current.processPayment(data);
      log('submitPayment', 'tokenResponse:', tokenResponse);

      if (!tokenResponse?.token?.id || tokenResponse?.error) {
        if (onError) {
          onError({
            processPayment: tokenResponse?.error || t('paymentMethodError'),
          });
        }
        return;
      }

      const { token } = tokenResponse;
      log('submitPayment', 'token:', token);

      const { error, paymentMethod } =
        await paymentProviderRef.current.createPaymentMethod(
          SupportedPaymentMethods.CARD,
          data,
        );
      log('submitPayment', 'paymentMethod:', paymentMethod);
      log('submitPayment', 'error:', error);

      if (error) {
        if (onError) {
          onError({ createPaymentMethod: error || t('paymentMethodError') });
        }
        return;
      }

      // Get payment method hash
      const { hash: paymentMethodHash, error: hashError } =
        await queryPaymentMethodHash(
          +propertyId,
          apiKey,
          {
            type: 'stripe_token',
            stripe_token: {
              token: token.id,
            },
          },
          apiUrl,
        );
      log('submitPayment', 'paymentMethodHash:', paymentMethodHash);
      log('submitPayment', 'hashError:', hashError);

      if (hashError || !paymentMethodHash) {
        if (onError) {
          onError({ hashError: hashError || t('paymentMethodError') });
        }
        return;
      }

      if (onSubmit) {
        const responseData: PaymentElementResponse = {
          ...data,
          ...(token?.id && { token: token.id }),
          paymentMethod: 'card',
          paymentMethodId: paymentMethod?.id,
          paymentMethodHash,
        };
        log('submitPayment', '[onSubmit=true] -> responseData:', responseData);

        const nextAction: NextAction = await onSubmit({ ...responseData });
        log('submitPayment', '[onSubmit=true] -> nextAction:', nextAction);

        await nextActionHandler(nextAction);

        // Resolve the submit promise with the result
        if (submitPromiseResolve.current) {
          if (returnTokenOnly) {
            // Return only the token/hash data
            submitPromiseResolve.current({
              token: token?.id,
              paymentMethodId: paymentMethod?.id,
              paymentMethodHash,
            });
          } else {
            submitPromiseResolve.current({
              success: true,
              paymentData: responseData,
              nextAction,
            });
          }
        }
      }
    };

    const submitErrorHandler: SubmitHandler<FormInputsType> = async () => {
      log('submitErrorHandler', 'paymentProviderRef:', paymentProviderRef);
      // TODO: Check if invalid: test isn't set on PaymentProvider-returned ref
      await paymentProviderRef.current.test('submitErrorHandler');

      log('submitErrorHandler', 'onError:', onError);
      if (onError) onError(sanitizeErrors(formErrors) as any);

      // Resolve the submit promise with error
      if (submitPromiseResolve.current) {
        submitPromiseResolve.current({
          success: false,
          errors: sanitizeErrors(formErrors),
        });
      }
    };

    /**
     * Contents called in the upper scope.
     * See submit and validate in PaymentElement for their invocation.
     */
    React.useImperativeHandle(forwardRef, () => ({
      async submit() {
        log(' 🥌 submit', 'processor:', processor);

        return new Promise(resolve => {
          // Store the resolve function to be called when submission completes
          submitPromiseResolve.current = resolve;

          handleSubmit(submitPayment, submitErrorHandler)();
        });
      },
      async validate() {
        return trigger();
      },
    }));

    return (
      <>
        <PaymentProvider
          processor={processor as any}
          ref={paymentProviderRef}
        />
        <FormProvider {...formMethods}>
          <div>
            {contactInformationConfig && (
              <Heading
                className="section-heading"
                paddingTop={(layoutProps as any).gap}
                paddingBottom={(layoutProps as any).gap}
              >
                {t('contactInformation')}
              </Heading>
            )}

            {contactInformationConfig && (
              <Grid
                templateColumns="repeat(4, 1fr)"
                templateRows="repeat(auto-fill, 1fr)"
                gap={(layoutProps as any).gap}
                columnGap={(layoutProps as any).columnGap}
              >
                {contactInformationConfig?.firstName && (
                  <GridItem colSpan={2}>
                    <InputControl
                      name="firstName"
                      label={t('firstName')}
                      error={unpackErrorMessage(formErrors, 'firstName')}
                      onChange={onFieldChange}
                      onBlur={onFieldBlur}
                      isRequired={contactInformationConfig?.firstName?.required}
                    />
                  </GridItem>
                )}
                {contactInformationConfig?.lastName && (
                  <GridItem colSpan={2}>
                    <InputControl
                      name="lastName"
                      label={t('lastName')}
                      error={unpackErrorMessage(formErrors, 'lastName')}
                      onChange={onFieldChange}
                      onBlur={onFieldBlur}
                      isRequired={contactInformationConfig?.lastName?.required}
                    />
                  </GridItem>
                )}
              </Grid>
            )}

            <Heading
              className="section-heading"
              paddingTop={(layoutProps as any).gap}
              paddingBottom={(layoutProps as any).gap}
            >
              {t('paymentDetails')}
            </Heading>

            <Grid
              templateColumns="repeat(4, 1fr)"
              templateRows="repeat(auto-fill, 1fr)"
              gap={(layoutProps as any).gap}
              columnGap={(layoutProps as any).columnGap}
            >
              <GridItem colSpan={4}>
                <InputControl
                  name="cardholder"
                  label={t('cardholderName')}
                  error={unpackErrorMessage(formErrors, 'cardholder')}
                  onChange={onFieldChange}
                  onBlur={onFieldBlur}
                  isRequired
                />
              </GridItem>

              <GridItem colSpan={4}>
                {/* CREDIT CARD NUMBER FIELD */}
                {processor === SupportedProcessors.STRIPE && (
                  <StripeCreditCardInputControl
                    id="card-number"
                    inputType="card"
                    name="cardNumber"
                    label={t('cardNumber')}
                    error={unpackErrorMessage(formErrors, 'cardNumber')}
                    onChange={onFieldChange}
                    onBlur={onFieldBlur as any}
                    theme={stripeTheme?.cardInput}
                    isRequired
                    rules={{
                      validate: {
                        cardNumber: (event: any) => {
                          const brands = cleanBrands(
                            paymentMethodConfig?.brands,
                          );
                          if (event?.brand && brands?.length > 0) {
                            if (
                              !brands.includes(event.brand) &&
                              !(event.brand === 'unknown')
                            ) {
                              const brandList = brands.join(', ');
                              return `${t('unsupportedCardType')} ${t('creditCardTypesAccepted')}: ${brandList}`;
                            }
                          }
                          return true;
                        },
                      },
                    }}
                  />
                )}

                {processor === SupportedProcessors.ADYEN && (
                  <CardInputControl
                    name="cardNumber"
                    label={t('cardNumber')}
                    error={unpackErrorMessage(formErrors, 'cardNumber')}
                    onChange={onFieldChange}
                    onBlur={onFieldBlur}
                    placeholder="1234 1234 1234 1234"
                    isRequired
                  />
                )}
              </GridItem>

              <GridItem colSpan={2}>
                {processor === SupportedProcessors.STRIPE && (
                  <StripeCreditCardInputControl
                    id="card-expiry"
                    inputType="expiry"
                    name="expiry"
                    label={t('cardExpire')}
                    error={unpackErrorMessage(formErrors, 'expiry')}
                    onChange={onFieldChange}
                    onBlur={onFieldBlur as any}
                    theme={stripeTheme?.expiryInput}
                    isRequired
                  />
                )}

                {processor === SupportedProcessors.ADYEN && (
                  <InputControl
                    name="expiry"
                    label={t('cardExpire')}
                    error={unpackErrorMessage(formErrors, 'expiry')}
                    rules={{
                      validate: {
                        valid: (val: any) => {
                          const year = new Date().getFullYear() % 100;
                          const month = new Date().getMonth() + 1;
                          const values = val?.split('/') || [];
                          if (+values[1] > year && +values[0] <= 12)
                            return true;
                          if (
                            +values[1] === year &&
                            +values[0] <= 12 &&
                            +values[0] >= month
                          )
                            return true;
                          return 'Invalid date';
                        },
                      },
                    }}
                    onChange={onFieldChange}
                    onBlur={onFieldBlur}
                    placeholder="MM/YY"
                    mask="00/00"
                    isRequired
                  />
                )}
              </GridItem>

              <GridItem colSpan={2}>
                {processor === SupportedProcessors.STRIPE && (
                  <StripeCreditCardInputControl
                    id="card-cvc"
                    inputType="cvc"
                    name="cvc"
                    label="CVC"
                    error={unpackErrorMessage(formErrors, 'cvc')}
                    onChange={onFieldChange}
                    onBlur={onFieldBlur}
                    theme={stripeTheme?.cvcInput}
                    isRequired
                  />
                )}

                {processor === SupportedProcessors.ADYEN && (
                  <InputControl
                    name="cvc"
                    label="CVC"
                    error={unpackErrorMessage(formErrors, 'cvc')}
                    rules={{
                      minLength: {
                        value: 3,
                        message: '3 digits',
                      },
                    }}
                    onChange={onFieldChange}
                    onBlur={onFieldBlur}
                    placeholder="000"
                    mask="0000"
                    isRequired
                  />
                )}
              </GridItem>
            </Grid>

            {hasBillingConfig && (
              <Heading
                className="section-heading"
                paddingTop={(layoutProps as any).gap}
                paddingBottom={(layoutProps as any).gap}
              >
                {t('billingAddressDetails')}
              </Heading>
            )}

            {hasBillingConfig && (
              <Grid
                templateColumns="repeat(2, 1fr)"
                templateRows="repeat(auto-fill, 1fr)"
                gap={(layoutProps as any).gap}
                columnGap={(layoutProps as any).columnGap}
              >
                {guest && (
                  <GridItem colSpan={2}>
                    <CheckboxControl
                      name="fill_billing_details"
                      label={t('sameAsAddress')}
                      onChange={onSameAsAddressChange}
                    />
                  </GridItem>
                )}
                {billingConfig?.billingAddress1 && (
                  <GridItem colSpan={2}>
                    <InputControl
                      name="billingAddress1"
                      label={t('billingStreetAddress')}
                      error={unpackErrorMessage(formErrors, 'billingAddress1')}
                      onChange={onFieldChange}
                      onBlur={onFieldBlur}
                      isRequired={billingConfig?.billingAddress1?.required}
                      isDisabled={
                        sameAsAddress && guestDataAsBilling.billingAddress1
                      }
                    />
                  </GridItem>
                )}
                {billingConfig?.billingAddress2 && (
                  <GridItem colSpan={2}>
                    <InputControl
                      name="billingAddress2"
                      label={t('billingStreetAddress2')}
                      error={unpackErrorMessage(formErrors, 'billingAddress2')}
                      onChange={onFieldChange}
                      onBlur={onFieldBlur}
                      isRequired={billingConfig?.billingAddress2?.required}
                      isDisabled={
                        sameAsAddress && guestDataAsBilling.billingAddress2
                      }
                    />
                  </GridItem>
                )}
                {billingConfig?.billingCountry && (
                  <GridItem colSpan={2}>
                    <SelectControl
                      name="billingCountry"
                      label={t('billingCountry')}
                      error={unpackErrorMessage(formErrors, 'billingCountry')}
                      onChange={onFieldChange}
                      onBlur={onFieldBlur}
                      list={countryList}
                      isRequired={billingConfig?.billingCountry?.required}
                      isDisabled={
                        sameAsAddress && guestDataAsBilling.billingCountry
                      }
                    />
                  </GridItem>
                )}
                {billingConfig?.billingState && (
                  <GridItem>
                    <InputControl
                      name="billingState"
                      label={t('billingState')}
                      error={unpackErrorMessage(formErrors, 'billingState')}
                      onChange={onFieldChange}
                      onBlur={onFieldBlur}
                      isRequired={billingConfig?.billingState?.required}
                      isDisabled={
                        sameAsAddress && guestDataAsBilling.billingState
                      }
                    />
                  </GridItem>
                )}
                {billingConfig?.billingCity && (
                  <GridItem>
                    <InputControl
                      name="billingCity"
                      label={t('billingCity')}
                      error={unpackErrorMessage(formErrors, 'billingCity')}
                      onChange={onFieldChange}
                      onBlur={onFieldBlur}
                      isRequired={billingConfig?.billingCity?.required}
                      isDisabled={
                        sameAsAddress && guestDataAsBilling.billingCity
                      }
                    />
                  </GridItem>
                )}
                {billingConfig?.billingZip && (
                  <GridItem>
                    <InputControl
                      name="billingZip"
                      label={t('billingZip')}
                      error={unpackErrorMessage(formErrors, 'billingZip')}
                      onChange={onFieldChange}
                      onBlur={onFieldBlur}
                      isRequired={billingConfig?.billingZip?.required}
                      isDisabled={
                        sameAsAddress && guestDataAsBilling.billingZip
                      }
                    />
                  </GridItem>
                )}
                {billingConfig?.billingEmail && (
                  <GridItem colSpan={2}>
                    <InputControl
                      name="billingEmail"
                      label={t('billingEmail')}
                      error={unpackErrorMessage(formErrors, 'billingEmail')}
                      onChange={onFieldChange}
                      onBlur={onFieldBlur}
                      rules={{
                        validate: (val: any) =>
                          val.trim() === '' ||
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                            val,
                          ) ||
                          t('invalidEmail'),
                      }}
                      isRequired={billingConfig?.billingEmail?.required}
                      isDisabled={
                        sameAsAddress && guestDataAsBilling.billingEmail
                      }
                    />
                  </GridItem>
                )}
              </Grid>
            )}
          </div>
        </FormProvider>
      </>
    );
  },
);
